'use client';

import { Indicator } from '$components/Indicator';
import { BackDropContent } from '$components/Navigation/BackDropContent';
import { ScrollButton } from '$components/ScrollButton';
import { LayoutContext } from '$root/src/context';
import cn from 'classnames';
import React, {
  Children,
  HTMLAttributes,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { HeroContext } from './context';
import { IHero } from './types';

export function HeroWrapper(props: IHero & HTMLAttributes<HTMLElement>) {
  const ref = useRef();
  const useStore = useContext(HeroContext);

  const offset = useStore((state) => state.offset);
  const setOffset = useStore((state) => state.setOffset);

  const currentPage = useStore((state) => state.currentPage);
  const setCurrentPage = useStore((state) => state.setCurrentPage);

  const setFromBG = useStore((state) => state.setFromBG);

  const alt = useStore((state) => state.alt);
  const setAlt = useStore((state) => state.setAlt);

  const canScroll = useStore((state) => state.canScroll);
  const setCanScroll = useStore((state) => state.setCanScroll);

  const setFirstLoad = useStore((state) => state.setFirstLoad);

  const setCurrentAnimation = useStore((state) => state.setCurrentAnimation);
  const setCurrentInnerAnimation = useStore(
    (state) => state.setCurrentInnerAnimation,
  );

  const useLayoutStore = useContext(LayoutContext);
  const menuTheme = useLayoutStore((state) => state.menuTheme);
  const newHeight = useLayoutStore((state) => state.resized);

  const {
    //heroType = 'slides',
    scrollCta,
    children,
    className,
    ...rest
  } = props;

  const actionChange = (from, to) => {
    setFromBG(() => from);
    setCurrentPage(() => to);
    setAlt((alt) => !alt);

    if (from < to) {
      setCurrentAnimation(() => cn('spinner_page' + (alt ? '_alt' : '')));
    } else {
      setCurrentAnimation(() =>
        cn('spinner_page_backward' + (alt ? '_alt' : '')),
      );
    }
    setCurrentInnerAnimation(() => cn('zoomBG_page' + (alt ? '_alt' : '')));

    setFirstLoad(() => false);
  };

  const onClick = (e) => {
    if (currentPage === parseInt(e.target.dataset.page)) return;
    actionChange(currentPage, parseInt(e.target.dataset.page));
  };

  const scrollTimer = useRef(null);

  const scrollActionTimer = () => {
    setCanScroll(() => false);
    clearTimeout(scrollTimer.current);
    scrollTimer.current = setTimeout(() => {
      setCanScroll(() => true);
    }, 2000);
  };

  const doAction = (delta) => {
    if (delta > 0 && currentPage > 0) {
      actionChange(currentPage, currentPage - 1);
    } else if (delta < 0 && currentPage < Children.count(children) - 1) {
      actionChange(currentPage, currentPage + 1);
    }
  };

  const scrollChange = (e) => {
    if (canScroll) {
      scrollActionTimer();
      doAction(e.nativeEvent.wheelDelta);
    }
  };

  let touchStart = 0;
  const moveChange = (e) => {
    let currentY = e.nativeEvent.touches[0].pageY;
    let delta = currentY - touchStart;
    if (canScroll) {
      scrollActionTimer();
      doAction(delta);
    }
  };
  const startTouch = (e) => {
    touchStart = e.nativeEvent.touches[0].pageY;
  };

  useEffect(() => {
    setOffset(() => window.pageYOffset);
  }, [setOffset]);

  useEffect(() => {
    const onScroll = () => setOffset(() => window.pageYOffset);
    // clean up code
    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, [setOffset]);

  useEffect(() => {
    if (currentPage < Children.count(children) - 1 && offset <= 0) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = window.innerHeight + 'px';
    } else {
      const timer = setTimeout(() => {
        document.body.style.overflow = 'auto';
        document.body.style.height = 'auto';
      }, 750);

      return () => clearTimeout(timer);
    }
  });

  return (
    <>
      <section
        {...rest}
        ref={ref}
        className={cn(
          className,
          menuTheme,
          menuTheme === 'dark' ? 'bg-black' : '',
          //'slidesContainer bg-black w-[calc(100%_+_70px)] md:w-[calc(100%_+_140px)] -mt-[35px] mb-[35px] -ml-[35px] md:-mt-[70px] md:mb-[70px] md:-ml-[70px]',
          'slidesContainer',
        )}
        onWheel={scrollChange}
        onTouchMove={moveChange}
        onTouchStart={startTouch}
        style={{
          height: newHeight,
          //window.innerWidth < 1024 ? window.innerHeight + 'px' : '100vh',
        }}
      >
        <div className={cn('mainMenu right-0 md:right-[52px] absolute')}>
          <div className={cn('slideBullets')}>
            {children instanceof Array && children.length > 1
              ? children.map((d, index) => (
                  <Indicator
                    status={currentPage === index ? 'active' : 'inactive'}
                    key={index}
                    index={index}
                    action={onClick}
                  />
                ))
              : null}
          </div>
        </div>
        {children}
        <div
          className={cn(
            'absolute bottom-10 lg:bottom-8 left-0 right-0 flex justify-center m-auto z-[5]',
          )}
        >
          {scrollCta !== null && scrollCta.trim() !== '' && (
            <ScrollButton size={'m'}>{scrollCta}</ScrollButton>
          )}
        </div>
      </section>
      <BackDropContent />
    </>
  );
}
